import { useMemo, useState } from 'react';
import { ActionIcon, Badge, Button, Card, Group, Stack, Switch, Text, Tooltip } from '@mantine/core';
import { IconPlus, IconRefresh } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { api, unwrap } from '../../api/client';
import type { AlertEvent, AlertRule } from '../../api/types';
import { useSession } from '../../app/session';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDateTime } from '../../lib/format';
import { notifyError, notifyInfo } from '../../lib/notify';
import { AlertRuleDrawer } from './AlertRuleDrawer';

/**
 * One aircraft's alerts, as its maintainer sees them.
 *
 * The list covers rules on the aircraft itself and on its maintenance tasks, because from the
 * hangar those are the same question: what is this aeroplane about to need?
 */
export function AlertRuleList({ aircraftId }: { aircraftId: number }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can } = useSession();
  const [ruleId, setRuleId] = useState<number | null>(null);
  const [isOpen, setOpen] = useState(false);

  const rules = useQuery({
    queryKey: ['alerts', 'rules', 'aircraft', aircraftId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/alerts/rules/', {
          params: { query: { aircraft: aircraftId, page_size: 100, ordering: 'name' } },
        }),
      ),
  });

  const events = useQuery({
    queryKey: ['alerts', 'events', 'aircraft', aircraftId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/alerts/events/', {
          params: {
            query: {
              aircraft: aircraftId,
              open: true,
              page_size: 50,
              ordering: '-fired_at',
            },
          },
        }),
      ),
  });

  const evaluate = useMutation({
    mutationFn: async (id: number) =>
      unwrap(
        await api.POST('/api/v1/alerts/rules/{id}/evaluate/', { params: { path: { id } } }),
      ),
    onSuccess: async (data) => {
      notifyInfo(
        data.fired
          ? (data.event?.message ?? t('alerts.ACTIVE'))
          : (data.reading?.label ?? t('maintenance.OK')),
      );
      await queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: notifyError,
  });

  const toggle = useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) =>
      unwrap(
        await api.PATCH('/api/v1/alerts/rules/{id}/', {
          params: { path: { id } },
          body: { is_active: isActive },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['alerts'] });
    },
    onError: notifyError,
  });

  const columns = useMemo<ColumnDef<AlertRule, unknown>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('common.name'),
        cell: ({ row }) => (
          <Stack gap={0}>
            <Text fz="sm" fw={600}>
              {row.original.name}
            </Text>
            <Text fz="xs" c="dimmed">
              {row.original.metric_label}
            </Text>
          </Stack>
        ),
      },
      {
        id: 'subject',
        header: t('alerts.subject'),
        meta: { width: 180 },
        cell: ({ row }) => (
          <Text fz="sm" className="identifier">
            {row.original.subject?.label}
          </Text>
        ),
      },
      {
        id: 'reading',
        header: t('alerts.currentReading'),
        meta: { width: 170 },
        cell: ({ row }) => {
          const reading = row.original.current_reading;
          if (!reading) return null;
          return (
            <Stack gap={0}>
              <Text fz="sm" ff="monospace">
                {reading.value ?? '—'}
                {reading.unit ? ` ${reading.unit}` : ''}
              </Text>
              {reading.remaining ? (
                <Text fz="xs" c={reading.is_breached ? 'red' : 'dimmed'}>
                  {t('maintenance.remaining')}: {reading.remaining}
                </Text>
              ) : null}
            </Stack>
          );
        },
      },
      {
        accessorKey: 'lead_times',
        header: t('alerts.leadTimes'),
        meta: { width: 140 },
        cell: ({ row }) => (
          <Group gap={4} wrap="wrap">
            {(row.original.lead_times ?? []).map((value, index) => (
              <Badge key={index} size="xs" variant="outline">
                {value}
              </Badge>
            ))}
          </Group>
        ),
      },
      {
        accessorKey: 'severity',
        header: t('alerts.severity'),
        meta: { width: 110 },
        cell: ({ row }) => <StatusBadge kind="severity" value={row.original.severity} size="sm" />,
      },
      {
        accessorKey: 'is_active',
        header: t('common.active'),
        meta: { width: 90 },
        cell: ({ row }) => (
          <Switch
            size="sm"
            checked={row.original.is_active}
            disabled={!can('alerts.manage_rules')}
            onChange={(event) =>
              toggle.mutate({ id: row.original.id, isActive: event.currentTarget.checked })
            }
            onClick={(event) => event.stopPropagation()}
          />
        ),
      },
      {
        id: 'actions',
        header: '',
        meta: { width: 50 },
        cell: ({ row }) => (
          <Tooltip label={t('alerts.evaluateNow')} withArrow>
            <ActionIcon
              variant="subtle"
              size="sm"
              loading={evaluate.isPending}
              onClick={(event) => {
                event.stopPropagation();
                evaluate.mutate(row.original.id);
              }}
            >
              <IconRefresh size={15} />
            </ActionIcon>
          </Tooltip>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, can],
  );

  const openEvents: AlertEvent[] = events.data?.results ?? [];

  return (
    <Stack gap="md">
      {openEvents.length ? (
        <Card p="md">
          <Text fw={600} mb="sm">
            {t('alerts.events')}
          </Text>
          <Stack gap="xs">
            {openEvents.map((event) => (
              <Group key={event.id} justify="space-between" wrap="nowrap">
                <Group gap="sm" wrap="nowrap">
                  <StatusBadge kind="severity" value={event.severity} size="sm" />
                  <Stack gap={0}>
                    <Text fz="sm" fw={500}>
                      {event.title}
                    </Text>
                    <Text fz="xs" c="dimmed">
                      {event.message}
                    </Text>
                  </Stack>
                </Group>
                <Text fz="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {formatDateTime(event.fired_at)}
                </Text>
              </Group>
            ))}
          </Stack>
        </Card>
      ) : null}

      {can('alerts.manage_rules') ? (
        <Button
          variant="light"
          size="compact-sm"
          leftSection={<IconPlus size={14} />}
          style={{ alignSelf: 'flex-start' }}
          onClick={() => {
            setRuleId(null);
            setOpen(true);
          }}
        >
          {t('alerts.newRule')}
        </Button>
      ) : null}

      <DataTable
        data={rules.data?.results ?? []}
        columns={columns}
        isLoading={rules.isLoading}
        onRowClick={
          can('alerts.manage_rules')
            ? (row) => {
                setRuleId(row.id);
                setOpen(true);
              }
            : undefined
        }
        emptyTitle={t('alerts.rules')}
        emptyHint={t('alerts.noAlerts')}
        minWidth={950}
      />

      <AlertRuleDrawer
        opened={isOpen}
        onClose={() => setOpen(false)}
        ruleId={ruleId}
        defaultSubject={{ model: 'aircraft.aircraft', id: aircraftId }}
      />
    </Stack>
  );
}
